/**
 * /reports/claim-imports — นำเข้ารายงานตามเบิก (ประกัน)
 *
 * The history of every insurer import, and the drag-drop that creates one.
 *
 * ⚠️ THE PAYER IS PICKED, NOT SNIFFED. The header signature validates the choice rather
 * than dispatching on it — because that same signature is what must REFUSE a drifted
 * file, and a file that matches no adapter would otherwise be unattributable to any
 * payer, unfilterable in this list, and unnameable in the alert about the drift.
 *
 * ⚠️ A FAILED RUN RENDERS AS A ROW, never as an absence. Otherwise "nothing happened" and
 * "it broke" look identical on this screen.
 *
 * ⚠️ THE MONEY COLUMN IS WHAT WAS WRITTEN, not the file's own total. They differ, and the
 * file total flatters — a run can parse ฿9,330 and legitimately write ฿0.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { Upload, FileSpreadsheet, AlertTriangle, CheckCircle2, Clock, Loader2, Bot, Trash2, Eye, RefreshCw, Search, X, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react'
import { usePersistedState } from '../../lib/persist.js'
import Pager from '../../components/Pager.jsx'
import {
  SOURCES, RUN_STATUSES, sourceLabel, monthOptions, monthLabel,
  readRunFilters, runQueryString, filterRuns, runSentAt,
} from '../../lib/claimRunQuery.js'
import ConfirmDialog from '../../components/ConfirmDialog.jsx'
// ⚠️ Extracted, not inlined: this list mirrors `ADAPTERS` in functions/lib/ across the
// bundle split, and only a plain .js file can be imported by the drift test that guards it.
// See src/data/claimImportPayers.js.
import { PAYERS } from '../../data/claimImportPayers.js'
import { runOpen, runPhase, runHasLinkRecords, RUN_PHASE } from '../../lib/claimLink.js'


/**
 * Can this run be thrown away?
 *
 * ⚠️ MIRRORS api 635's allow-list DELIBERATELY, and is NOT the gate. Xano refuses on the
 * same two conditions, so a run that slips past this predicate still cannot be deleted.
 * What this decides is only whether the button is worth showing. Statuses that wrote, or
 * may have written, billing rows are absent on purpose — `committed`,
 * `partially_committed`, `reverted`, `partially_reverted` are records of money that moved.
 */
const DELETABLE = new Set(['staged', 'parse_failed', 'rejected'])

const PER_PAGE = 50
const REVERTED = new Set(['reverted', 'partially_reverted'])
/* ⚠️ `rows_written == 0` IS NOT "WROTE NOTHING" ANY MORE. ผูก never moves that counter, so
   on 2026-09-17 three `staged` runs showed a trash can while holding link records — #93 had
   8 rows linked as `received`, real money written. Deleting one orphans its link events.
   Unknown (the marks failed to load) counts as "has records": deny by default. */
const canDiscard = (r) => DELETABLE.has(r?.status) && Number(r?.rows_written || 0) === 0
  && runHasLinkRecords(r) === false

const fmt = (n) => Number(n || 0).toLocaleString('th-TH', { maximumFractionDigits: 2 })
const dt = (v) => (v ? new Date(Number(v)).toLocaleString('th-TH', { dateStyle: 'medium', timeStyle: 'short' }) : '—')
/* วันที่ส่ง — the rule (email date, else the file's own date) lives in claimRunQuery.js
   #runSentAt so the sort and the cell can never disagree. */
function sentDate(r) {
  const s = runSentAt(r)
  if (!s) return null
  if (s.kind === 'email') return { text: dt(s.ms), note: 'อีเมล' }
  const text = new Date(s.ms).toLocaleDateString('th-TH', { dateStyle: 'medium', timeZone: 'Asia/Bangkok' })
  return { text, note: r.payer === 'AIA' ? 'ตามจดหมาย' : 'ตามไฟล์' }
}

/**
 * ⚠️ THE CHIP READS THE DERIVED PHASE, NOT `run.status`, AND THAT IS THE POINT OF THIS SCREEN.
 * `committed` means "somebody pressed the button once". It does NOT mean the file is
 * accounted for: commit.js writes only the rows that were ticked, and the pre-tick rule is
 * high-confidence-only — which AIA and Generali structurally never are, because their files
 * carry no per-claim reference. Measured 2026-09-15: 65 of 78 runs still hold unaccounted
 * money and this chip called 47 of them บันทึกแล้ว, in green.
 * The raw `run.status` survives ONLY where it is genuinely the right question: `canDiscard`
 * (api 635 refuses anything but staged/parse_failed/rejected) and the reverted exclusion in
 * `totals`. The word บันทึกแล้ว is gone from this screen.
 */
const PHASE_ICON = { open: Clock, done: CheckCircle2, parse_failed: AlertTriangle, rejected: AlertTriangle, empty: Clock }

function StatusChip({ run }) {
  const phase = runPhase(run)
  const s = RUN_PHASE[phase] || RUN_PHASE.empty
  const Icon = PHASE_ICON[phase] || Clock
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[12px] font-medium ${s.cls}`}>
      <Icon size={13} strokeWidth={2} />{s.label}
    </span>
  )
}

export default function ClaimImports() {
  const navigate = useNavigate()
  /* ⚠️ localStorage, NOT router state. Unlike Reimbursement (which opens the patient in a
     NEW tab and never unmounts), this page unmounts on navigate, and the trip back is a
     <Link> PUSH from the run page (ClaimImportRun.jsx:192) that carries nothing — while
     browser-Back is a POP that would carry the state of the ORIGINAL entry. The two return
     paths would disagree, and a reload would lose both. The row's existing
     `focus:bg-brand-50/40` cannot serve either: focus returns to <body> after a back nav. */
  const [lastOpened, setLastOpened] = usePersistedState('claimrun:lastOpened', null)

  /* ONE opener, called from both the row click and the Enter key, so keyboard activation
     marks the row too (mirrors CallList.jsx). Step H appends the filter query string here. */
  const openRun = (r) => {
    setLastOpened(r.id)
    // Carry the filters through so prev/next walks this list and ย้อนกลับ comes back here.
    const qs = runQueryString(filters, { ci_page: safePage })
    navigate(`/reports/claim-imports/${r.id}${qs ? `?${qs}` : ''}`)
  }
  const [runs, setRuns] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadErr, setLoadErr] = useState('')
  const [progressWarn, setProgressWarn] = useState('')
  /* ⚠️ Filters live in the URL, namespaced `ci_`, so prev/next on the run page can walk the
     SAME filtered list and ย้อนกลับ can return to the same page of it. The ledger tab owns
     bare `page`/`payer` in its own state — the namespace is what keeps them from colliding
     now that all three pages share one URL. */
  const [params, setParams] = useSearchParams()
  const filters = readRunFilters(params)
  const { payer: payerFilter, source: sourceFilter, status: statusFilter, month: monthFilter } = filters

  /* The search box types into LOCAL state and reaches the URL a beat later. Writing every
     keystroke straight to the URL fights the Thai IME (the composing vowel gets committed
     early) and resets ci_page on each letter. */
  const [qDraft, setQDraft] = useState(filters.q)
  useEffect(() => { setQDraft(filters.q) }, [filters.q])
  useEffect(() => {
    if (qDraft.trim() === filters.q) return undefined
    const t = setTimeout(() => setFilter('ci_q', qDraft.trim()), 250)
    return () => clearTimeout(t)
  }, [qDraft]) // eslint-disable-line react-hooks/exhaustive-deps

  /* Merging setter, never the object form — setParams({...}) would wipe `tab` and drop the
     operator back on the ledger. Any filter change resets to page 1. */
  const setFilter = (key, v) => setParams((prev) => {
    const n = new URLSearchParams(prev)
    if (v) n.set(key, v); else n.delete(key)
    n.delete('ci_page')
    return n
  }, { replace: true })
  const setPage = (n) => setParams((prev) => {
    const q = new URLSearchParams(prev)
    if (n > 1) q.set('ci_page', String(n)); else q.delete('ci_page')
    return q
  }, { replace: true })
  const [robot, setRobot] = useState(null)

  const [payer, setPayer] = useState('axa')
  const [transferDate, setTransferDate] = useState('')
  const [file, setFile] = useState(null)
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState(null)
  const [dragOver, setDragOver] = useState(false)
  const [pendingDelete, setPendingDelete] = useState(null)
  const [deleting, setDeleting] = useState(false)
  const [deleteErr, setDeleteErr] = useState('')
  const inputRef = useRef(null)

  const chosen = PAYERS.find((p) => p.key === payer)
  const needsDate = !!chosen?.needsDate
  const accept = chosen?.accept || '.xlsx'
  const ext = chosen?.ext || '.xlsx'

  const load = useCallback(async () => {
    setLoading(true); setLoadErr(''); setProgressWarn('')
    try {
      // 500, not 100: the other three filters are client-side, so everything they can
      // reach must already be loaded. `payer` stays on the wire — see claimRunQuery.js.
      const q = new URLSearchParams({ per_page: '500' })
      if (payerFilter) q.set('payer', payerFilter)
      const r = await fetch(`/api/claim-import/runs?${q}`, { credentials: 'same-origin' })
      if (r.status === 404) { setLoadErr('ยังไม่ได้เปิดใช้งานฝั่งเซิร์ฟเวอร์'); setRuns([]); return }
      const j = await r.json()
      if (!r.ok) { setLoadErr(j?.error || 'โหลดประวัติไม่สำเร็จ'); setRuns([]); return }
      const list = Array.isArray(j?.runs) ? j.runs : []
      setRuns(list)
      if (j?.progress_error) setProgressWarn(j.progress_error)
      // Never truncate silently — before this the page dropped everything past 100 runs
      // with nothing on screen to say so.
      if (list.length >= 500) setLoadErr('แสดงเฉพาะ 500 รอบล่าสุด — รอบที่เก่ากว่านี้ยังไม่ได้โหลด')
    } catch (e) {
      setLoadErr(String(e))
    } finally {
      setLoading(false)
    }
  }, [payerFilter])

  useEffect(() => { load() }, [load])

  const loadRobot = useCallback(() => {
    fetch('/api/claim-import/robot-status', { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => setRobot(j))
      .catch(() => {})
  }, [])

  /* ⚠️ There was NO refresh control on this page. Changing the payer filter was the only
     way to re-read, and now that three of the four filters are client-side even that is
     gone for most changes — on a screen whose own comment says a dead robot looks exactly
     like a quiet one. Refreshes the run list AND the courier pulse together. */
  const refreshAll = useCallback(() => { load(); loadRobot() }, [load, loadRobot])

  /* The courier's last-seen. ⚠️ A robot that finds nothing looks exactly like a robot that
     died — files arrive about twice a month, so an empty run list proves nothing either
     way. This is the only thing that tells them apart. */
  useEffect(() => {
    let alive = true
    fetch('/api/claim-import/robot-status', { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (alive) setRobot(j) })
      .catch(() => {})
    return () => { alive = false }
  }, [])

  async function upload() {
    if (!file || busy) return
    setBusy(true); setResult(null)
    try {
      const fd = new FormData()
      fd.append('file', file)
      fd.append('payer', payer)
      if (transferDate) fd.append('transfer_date', transferDate)
      const r = await fetch('/api/claim-import/upload', { method: 'POST', body: fd, credentials: 'same-origin' })
      const j = await r.json().catch(() => ({}))
      setResult({ status: r.status, ...j })
      if (j?.run_id) { setFile(null); if (inputRef.current) inputRef.current.value = ''; load() }
    } catch (e) {
      setResult({ error: String(e) })
    } finally {
      setBusy(false)
    }
  }

  /**
   * ⚠️ THE LIST IS RELOADED FROM THE SERVER, never patched locally. Splicing the row out of
   * `runs` would show a delete that succeeded even when Xano refused it — and the refusals
   * here (a run committed in another tab; a role that is not manager) are exactly the cases
   * worth seeing.
   */
  async function doDelete() {
    const run = pendingDelete
    if (!run || deleting) return
    setDeleting(true); setDeleteErr('')
    try {
      const r = await fetch(`/api/claim-import/run/${run.id}/delete`, {
        method: 'DELETE', credentials: 'same-origin',
      })
      const j = await r.json().catch(() => ({}))
      if (!r.ok) {
        // Xano's own Thai message says WHICH gate refused; prefer it over anything invented.
        setDeleteErr(j?.message || j?.detail || j?.error || `ลบไม่สำเร็จ (${r.status})`)
        return
      }
      setPendingDelete(null)
      load()
    } catch (e) {
      setDeleteErr(String(e))
    } finally {
      setDeleting(false)
    }
  }

  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false)
    const f = e.dataTransfer?.files?.[0]
    if (f) setFile(f)
  }

  /* ⚠️ THE SAME filterRuns() the run page's prev/next uses. If these two ever diverge,
     prev/next walks into runs that are not on the operator's screen — silently. */
  const filtered = useMemo(() => filterRuns(runs, filters),
    [runs, filters.payer, filters.source, filters.status, filters.month, filters.q, filters.sort]) // eslint-disable-line react-hooks/exhaustive-deps
  const pageCount = Math.max(1, Math.ceil(filtered.length / PER_PAGE))
  const safePage = Math.min(filters.page, pageCount)
  const pageRows = useMemo(
    () => filtered.slice((safePage - 1) * PER_PAGE, safePage * PER_PAGE), [filtered, safePage])

  /* Keep the URL honest after a delete or a filter change shrinks the list. `safePage`
     already clamps what is RENDERED — this only stops the address bar claiming ci_page=5
     on a two-page list, which would otherwise survive into a shared link. */
  useEffect(() => {
    if (filters.page > pageCount) setPage(pageCount)
  }, [filters.page, pageCount]) // eslint-disable-line react-hooks/exhaustive-deps

  const months = useMemo(() => monthOptions(runs), [runs])
  /* ⚠️ Union of the hand-maintained PAYERS mirror and the payers actually PRESENT in the
     data. That mirror has already drifted once in production — Prudential was live
     server-side for a day while invisible in this dropdown — so a payer must never be
     unfilterable just because someone forgot to add it here. */
  const payerOptions = useMemo(() => {
    const seen = new Map(PAYERS.map((p) => [p.payer, p.label]))
    for (const r of runs) if (r?.payer && !seen.has(r.payer)) seen.set(r.payer, r.payer)
    return [...seen].map(([v, label]) => ({ v, label }))
  }, [runs])

  /* ⚠️ Reduces `filtered`, not `runs`, so รอบ / บันทึกจริง / ยอดที่บันทึกจริง keep meaning
     the whole filtered set rather than silently becoming a per-page figure. */
  /* ⚠️ Excludes the reverted family. commit.js counted a revert as `rows_written` until
     2026-09-14, so a fully rolled-back run reported "5 saved · ฿0" and this clinic-wide sum
     inherited it. New runs are correct; historic reverted runs still carry the old number,
     which is why they are excluded here rather than trusted. */
  /* ⚠️ `openBaht` IS THE HEADLINE NUMBER AND IT NEEDS NO NEW ENDPOINT. `amount_parsed` is the
     sum of every row's file figure and `amount_written` the sum of the confirmed ones, so
     the difference is the money in these files that nobody has accounted for. Verified
     against the rows themselves on 2026-09-15: ฿532,108.99 from both sides, to the satang.
     Reverted runs are read as having written nothing — see claimLink.js. */
  /* ⚠️ 2026-09-17 — NOW FROM THE ROWS (runOpen), with the counters only as a fallback. The
     counters never saw a row closed by ผูก, and this strip read ฿584,058.99 open when the
     rows said ฿535,285.00. See claimLink.js#runOpenFromCounters. */
  const totals = useMemo(() => {
    const per = filtered.map(runOpen)
    return {
      runs: filtered.length,
      written: per.reduce((t, p) => t + p.done, 0),
      amount: per.reduce((t, p) => t + (Number(p.doneBaht) || 0), 0),
      reverted: filtered.filter((r) => REVERTED.has(r.status)).length,
      openRuns: filtered.filter((r) => runPhase(r) === 'open').length,
      openRows: per.reduce((t, p) => t + p.open, 0),
      openBaht: per.reduce((t, p) => t + p.openBaht, 0),
    }
  }, [filtered])

  /* The run the operator last opened may have left the filter while they were in it —
     commit a `รอยืนยัน` run and it becomes `committed`. Say so rather than just losing it. */
  const lostToFilter = useMemo(() => (
    lastOpened != null
      && runs.some((r) => r.id === lastOpened)
      && !filtered.some((r) => r.id === lastOpened)
  ), [lastOpened, runs, filtered])

  return (
    /* Chrome now belongs to ReimbursementHub.jsx. ⚠️ The safety promise that used to
       close the subtitle — ยังไม่มีการบันทึกใด ๆ จนกว่าจะกดยืนยัน — moved into
       that file's SUBTITLE.insurer. It must not be lost. */
    <div>

      {/* the courier's pulse -------------------------------------------------
          Rendered above the upload card on purpose: if the robot is down, that is the
          first thing anyone opening this page needs to know. */}
      {robot ? (
        <div className={`mb-5 flex flex-wrap items-center gap-2 rounded-xl border px-4 py-2.5 text-[13px] ${
          robot.never ? 'border-slate-200 bg-slate-50 text-slate-600'
            : robot.stale ? 'border-rose-200 bg-rose-50 text-rose-800'
              : 'border-emerald-200 bg-emerald-50 text-emerald-800'}`}
        >
          <Bot size={15} />
          {robot.never ? (
            <span>ยังไม่ได้ติดตั้งหุ่นยนต์ตรวจอีเมล — ตอนนี้ใช้การอัปโหลดเองเท่านั้น</span>
          ) : robot.stale ? (
            <span>
              <b>หุ่นยนต์ตรวจอีเมลหยุดทำงาน</b> — ตรวจล่าสุดเมื่อ {Math.round(robot.age_minutes)} นาทีที่แล้ว
              (ปกติทุก 1 ชั่วโมง) · ไฟล์ที่เข้ามาช่วงนี้อาจยังไม่ถูกนำเข้า
            </span>
          ) : (
            <span>
              หุ่นยนต์ตรวจอีเมลทำงานปกติ — ตรวจล่าสุด {Math.round(robot.age_minutes)} นาทีที่แล้ว
              {robot.last?.script_version ? ` · ${robot.last.script_version}` : ''}
            </span>
          )}
        </div>
      ) : null}

      {/* a delivery that FAILED ----------------------------------------------
          ⚠️ A SECOND STRIP, NOT A FOURTH ARM OF THE TERNARY ABOVE. "Is the robot alive"
          and "did the robot drop a file" are independent facts, and on 2026-09-14 they
          had opposite answers: the courier was beating perfectly on schedule while a
          Generali settlement it had already swallowed was being refused by the CRM. A
          single banner must choose one of the two to display, and whichever it chooses it
          is lying about the other. Two strips can both be true at once.

          That day this page rendered "หุ่นยนต์ตรวจอีเมลทำงานปกติ" in green while the
          browser was holding error_count: 1 and the ERROR_FATAL string — it had the
          evidence and dropped it on the floor. ฿1,000 went unimported for a day and was
          found only by querying Xano by hand. */}
      {robot?.last_error ? (
        <div className="mb-5 rounded-xl border border-rose-300 bg-rose-50 px-4 py-3 text-[13px] text-rose-900">
          <div className="flex items-center gap-2">
            <AlertTriangle size={15} className="shrink-0" />
            <b>หุ่นยนต์ส่งไฟล์เข้าระบบไม่สำเร็จ</b>
            <span className="text-rose-700">
              — พบอีเมล {robot.last_error.found_count ?? 0} ฉบับ · นำเข้าสำเร็จ{' '}
              {robot.last_error.posted_count ?? 0} · ผิดพลาด {robot.last_error.error_count ?? 0}
            </span>
          </div>
          {/* The raw upstream string, verbatim — same treatment the upload box already
              gives its errors. It is the only diagnosis anyone gets. */}
          {robot.last_error.detail ? (
            <div className="mt-2 rounded-lg bg-white/70 px-3 py-2 font-mono text-[11.5px] leading-relaxed text-rose-800 break-all">
              {robot.last_error.detail}
            </div>
          ) : null}
          <div className="mt-2 text-[12px] text-rose-700">
            ไฟล์ที่ส่งไม่สำเร็จจะไม่ถูกลองใหม่ — ค้นใน Gmail ด้วย{' '}
            <code className="rounded bg-white/70 px-1 font-mono">label:claim-import/failed</code>{' '}
            แล้วอัปโหลดไฟล์แนบเองที่ด้านล่าง
            {/* ⚠️ Says what the window actually covers rather than implying an all-clear
                it never checked for. api 633 returns one beat today, so "no error" here
                means "not in the last beat", NOT "nothing has ever failed". */}
            {robot.error_window_beats <= 1
              ? ' · (ตรวจสอบย้อนหลังได้เพียง 1 รอบล่าสุดเท่านั้น)'
              : ` · (ตรวจย้อนหลัง ${robot.error_window_beats} รอบ)`}
          </div>
        </div>
      ) : null}

      {/* upload ------------------------------------------------------------ */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm mb-8">
        <div className="flex flex-wrap items-end gap-4 mb-4">
          <label className="block">
            <span className="block text-[12.5px] font-medium text-slate-600 mb-1">บริษัทประกัน</span>
            <select
              value={payer}
              onChange={(e) => { setPayer(e.target.value); setResult(null); setFile(null) }}
              className="h-9 rounded-lg border border-slate-300 px-3 text-[14px] bg-white min-w-[180px]"
            >
              {PAYERS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
            </select>
          </label>

          {/* ⚠️ AXA's file carries NO transfer date anywhere — statementNo "2026-06-40" is
              a period label. Without this the stamp silently becomes the click date. */}
          {needsDate ? (
            <label className="block">
              <span className="block text-[12.5px] font-medium text-slate-600 mb-1">
                วันที่เงินเข้าบัญชี <span className="text-rose-600">*</span>
              </span>
              <input
                type="date"
                value={transferDate}
                onChange={(e) => setTransferDate(e.target.value)}
                className="h-9 rounded-lg border border-slate-300 px-3 text-[14px] bg-white"
              />
            </label>
          ) : null}
        </div>

        {/* ⚠️ Branch on the PAYER, never on `needsDate` alone. Two payers now carry no
            transfer date in the file, and this paragraph names one of them out loud —
            keying it on the flag printed "ไฟล์ของ AXA" over a GoWabi upload.
            ⚠️ AND THE LAST ARM IS `payer === 'generali'`, NOT A BARE `else`. It used to be
            the fallback, so every payer added after it inherited Generali's text: adding
            Prudential printed "ไฟล์ของ Generali มีคอลัมน์ Payment Date" over a .xls from
            BlueVenture. A payer with no arm now shows NO hint, which is merely unhelpful
            instead of actively wrong. */}
        {payer === 'axa' ? (
          <p className="text-[12.5px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-4">
            ไฟล์ของ AXA <b>ไม่มีวันที่โอนเงิน</b> อยู่ในไฟล์ ต้องกรอกวันที่จาก statement ของธนาคาร
            มิฉะนั้นระบบจะบันทึกวันที่กดปุ่มแทนวันที่เงินเข้าจริง
          </p>
        ) : payer === 'gowabi' ? (
          <p className="text-[12.5px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-4">
            ไฟล์ของ GoWabi (<b>Financial Report</b> รายเดือน ชีท <b>Finance Report</b>)
            {' '}<b>ไม่มีวันที่โอนเงิน</b> อยู่ในไฟล์ ต้องกรอกวันที่จาก statement ของธนาคาร
            <br />
            ระบบจับคู่ด้วย <b>Secret Code</b> (รหัสคูปอง) ที่พนักงานบันทึกไว้ใน <b>รหัสเคลม</b> หรือ
            {' '}<b>รายละเอียด</b> ของรายการ — <b>ไม่ใช่เลข Order</b> ซึ่งเป็นเลขตะกร้าและใช้ร่วมกันได้หลายรายการ
            <br />
            ยอดในไฟล์เป็นยอด <b>หลังหักค่าคอมมิชชั่น 20% + VAT 7%</b> แล้ว ซึ่งตรงกับยอดเบิกที่บันทึกในระบบอยู่แล้ว
          </p>
        ) : payer === 'aia' ? (
          <p className="text-[12.5px] text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 mb-4">
            ไฟล์ของ AIA เป็น <b>PDF</b> ที่ดาวน์โหลดจากระบบ AIA (Hospital Internet Claims)
            ระบบจะอ่าน <b>วันที่ตามจดหมายในหน้าแรก</b> เป็นวันที่โอนเงิน และอ่านรายชื่อจากหน้าที่ 2
            {' '}<b>ไม่ต้องแก้ไขไฟล์ก่อนอัปโหลด</b>
          </p>
        ) : payer === 'prudential' ? (
          <p className="text-[12.5px] text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 mb-4">
            ไฟล์ของ Prudential ส่งมาจาก <b>BlueVenture TPA</b> เป็น Excel <b>รุ่นเก่า (.xls)</b>
            {' '}— อัปโหลดไฟล์แนบจากอีเมลได้เลย <b>ห้ามเปิดแล้ว Save As เป็น .xlsx ก่อน</b>
            <br />
            ระบบอ่าน <b>วันที่โอนเงิน</b> จากคอลัมน์ <b>Paid Date</b> ในไฟล์ ไม่ต้องกรอกเอง
            <br />
            จับคู่ด้วย <b>Claim No</b> (ตัดท้าย <b>-02</b> ให้อัตโนมัติ) ถ้ารายการไหนไม่มีรหัสเคลมในระบบ
            {' '}จะใช้ <b>Bill No</b> แทน · ยอดที่ใช้จับคู่คือ <b>Incurred Amt (After Discount)</b>
            {' '}คือยอดที่คลินิก<b>เบิก</b> ไม่ใช่ยอดที่ประกันจ่ายจริง
            <br />
            {/* ⚠️ [measured] one payment cycle produced 4 files 13–38s apart, ALL WITH THE SAME
                FILENAME — one per branch. Without this line the operator sees four identical
                names in the mail, assumes they are duplicates, uploads one, and silently
                imports a quarter of the money. The dedupe keys on file contents, so a genuine
                re-upload is still caught. */}
            <b className="text-amber-800">1 ไฟล์ = 1 สาขา</b> — รอบเดียวกันมักมีหลายไฟล์
            {' '}<b>ชื่อไฟล์ซ้ำกัน</b> ต้องอัปโหลดให้ครบทุกไฟล์ (ไฟล์ซ้ำจริงระบบจะเตือนเอง)
          </p>
        ) : payer === 'generali' ? (
          <p className="text-[12.5px] text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 mb-4">
            ไฟล์ของ Generali มีคอลัมน์ <b>Payment Date</b> อยู่แล้ว ระบบจะใช้วันที่จากไฟล์
          </p>
        ) : null}

        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          onClick={() => inputRef.current?.click()}
          className={`rounded-xl border-2 border-dashed px-6 py-8 text-center cursor-pointer transition ${
            dragOver ? 'border-brand-400 bg-brand-50' : 'border-slate-300 hover:border-brand-300 bg-slate-50/60'}`}
        >
          <input
            ref={inputRef}
            type="file"
            accept={accept}
            className="hidden"
            onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null) }}
          />
          <FileSpreadsheet size={26} className="mx-auto text-slate-400 mb-2" strokeWidth={1.6} />
          {file ? (
            <div className="text-[14px] text-slate-800 font-medium">{file.name} <span className="text-slate-500 font-normal">({fmt(file.size)} bytes)</span></div>
          ) : (
            <div className="text-[14px] text-slate-500">ลากไฟล์ {ext} มาวาง หรือคลิกเพื่อเลือก</div>
          )}
        </div>

        <div className="mt-4 flex items-center gap-3">
          <button
            type="button"
            disabled={!file || busy || (needsDate && !transferDate)}
            onClick={upload}
            className="inline-flex items-center gap-2 h-9 px-4 rounded-lg bg-brand-600 text-white text-[14px] font-medium disabled:opacity-40 disabled:cursor-not-allowed hover:bg-brand-700 transition"
          >
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />}
            {busy ? 'กำลังตรวจไฟล์…' : 'ตรวจไฟล์'}
          </button>
          {needsDate && !transferDate ? (
            <span className="text-[12.5px] text-slate-500">กรอกวันที่เงินเข้าบัญชีก่อน</span>
          ) : null}
        </div>

        {result ? (
          <div className={`mt-4 rounded-xl border px-4 py-3 text-[13px] ${
            result.run_id && result.ok ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
              : result.run_id ? 'border-amber-200 bg-amber-50 text-amber-900'
                : 'border-rose-200 bg-rose-50 text-rose-800'}`}
          >
            {result.duplicate ? (
              <>ไฟล์นี้เคยนำเข้าแล้ว — <Link className="underline font-medium" to={`/reports/claim-imports/${result.run_id}`}>เปิดรอบเดิม #{result.run_id}</Link></>
            ) : result.run_id ? (
              <>
                <div className="font-medium">
                  {result.ok ? 'ตรวจไฟล์ผ่าน' : 'ไฟล์ไม่ผ่านการตรวจ'} — บันทึกเป็นรอบ #{result.run_id}
                </div>
                {result.totals ? (
                  <div className="mt-1">
                    ทั้งหมด {fmt(result.totals.rows_total)} แถว · พร้อมบันทึก {fmt(result.totals.rows_high + result.totals.rows_medium + result.totals.rows_low)} ·
                    รับแล้วอยู่ก่อน {fmt(result.totals.rows_already_received)} · กำกวม {fmt(result.totals.rows_ambiguous)} · จับคู่ไม่ได้ {fmt(result.totals.rows_unmatched)}
                  </div>
                ) : null}
                {result.error ? <div className="mt-1">สาเหตุ: {result.error}</div> : null}
                {result.archive_error ? <div className="mt-1 text-amber-800">⚠️ เก็บไฟล์ต้นฉบับไม่สำเร็จ ({result.archive_error}) — ตรวจสอบสิทธิ์ Wasabi</div> : null}
                <Link className="underline font-medium mt-1 inline-block" to={`/reports/claim-imports/${result.run_id}`}>เปิดดูรายละเอียดและยืนยัน →</Link>
              </>
            ) : (
              /* ⚠️ SHOW `detail`, NOT JUST `error`. `error` is a bucket name — `stage_failed`
                 is every possible upstream refusal wearing one label — and on 2026-08-27 a
                 real Xano 500 (`Unable to locate var: r.before_advance_status`) reached the
                 operator as the bare word "stage_failed", with the actual cause sitting
                 unread in the same response body. An error nobody can act on costs a
                 debugging session per occurrence. */
              <>
                <div className="font-medium">ผิดพลาด: {result.error || `HTTP ${result.status}`}</div>
                {result.detail ? (
                  <div className="mt-1 font-mono text-[12px] break-all opacity-90">
                    {result.upstream ? `HTTP ${result.upstream} · ` : ''}{result.detail}
                  </div>
                ) : null}
              </>
            )}
          </div>
        ) : null}
      </div>

      {/* history ------------------------------------------------------------ */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <h2 className="text-[16px] font-semibold text-slate-900 mr-auto">ประวัติการนำเข้า</h2>

        {/* Finds the RUN a patient's line came in on — see runMatchesPatient. */}
        <div className="relative">
          <Search size={14} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="search"
            value={qDraft}
            onChange={(e) => setQDraft(e.target.value)}
            placeholder="ค้นหา HN / ชื่อ / นามสกุล"
            aria-label="ค้นหาคนไข้"
            className="h-8 w-[220px] rounded-lg border border-slate-300 bg-white pl-8 pr-7 text-[13px] placeholder:text-slate-400"
          />
          {qDraft ? (
            <button type="button" onClick={() => setQDraft('')} aria-label="ล้างคำค้น"
              className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-0.5 text-slate-400 hover:text-slate-600">
              <X size={13} />
            </button>
          ) : null}
        </div>

        <select value={monthFilter} onChange={(e) => setFilter('ci_month', e.target.value)}
          aria-label="เดือนที่นำเข้า" className="h-8 rounded-lg border border-slate-300 px-2 text-[13px] bg-white">
          <option value="">ทุกเดือน</option>
          {months.map((m) => <option key={m} value={m}>{monthLabel(m)}</option>)}
        </select>

        <select value={sourceFilter} onChange={(e) => setFilter('ci_source', e.target.value)}
          aria-label="ที่มา" className="h-8 rounded-lg border border-slate-300 px-2 text-[13px] bg-white">
          <option value="">ทุกที่มา</option>
          {SOURCES.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
        </select>

        <select value={payerFilter} onChange={(e) => setFilter('ci_payer', e.target.value)}
          aria-label="บริษัท" className="h-8 rounded-lg border border-slate-300 px-2 text-[13px] bg-white">
          <option value="">ทุกบริษัท</option>
          {payerOptions.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
        </select>

        <select value={statusFilter} onChange={(e) => setFilter('ci_status', e.target.value)}
          aria-label="สถานะ" className="h-8 rounded-lg border border-slate-300 px-2 text-[13px] bg-white">
          <option value="">ทุกสถานะ</option>
          {RUN_STATUSES.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
        </select>

        {/* ⚠️ The ONLY refresh on this page. Three of the four filters above are client-side
            and never refetch, and the courier pulse is fetched once on mount. */}
        <button type="button" onClick={refreshAll} disabled={loading}
          title="โหลดประวัติและสถานะหุ่นยนต์ใหม่"
          className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-lg border border-slate-300 bg-white text-[13px] text-slate-600 hover:bg-slate-50 disabled:opacity-50">
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> รีเฟรช
        </button>
      </div>

      {lostToFilter ? (
        <div className="mb-3 rounded-xl border border-violet-200 bg-violet-50/70 px-4 py-2.5 text-[12.5px] text-violet-900">
          รอบที่เพิ่งเปิดไปไม่อยู่ในตัวกรองนี้แล้ว — สถานะอาจเปลี่ยนไปหลังจากบันทึก
        </div>
      ) : null}

      {/* ⚠️ Never a silent fallback. The counters miss every row closed by ผูก, so when the
          row marks fail to load the numbers below can be wrong in exactly the #88 way. */}
      {progressWarn ? (
        <div className="mb-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-[12.5px] text-amber-900">
          โหลดสถานะรายแถวไม่สำเร็จ — ตัวเลขด้านล่างอาจไม่นับรายการที่ผูกแล้ว และปุ่มลบถูกซ่อนไว้ กดรีเฟรชอีกครั้ง
          {filters.q ? ' · การค้นหาคนไข้ใช้ไม่ได้จนกว่าจะโหลดสำเร็จ' : ''}
        </div>
      ) : null}

      {totals.runs ? (
        <div className="flex flex-wrap gap-5 mb-3 text-[13px] text-slate-600">
          <span>{fmt(totals.runs)} รอบ</span>
          {/* ⚠️ จัดการแล้ว, not ผูกแล้ว — this counts rows whose MONEY is settled, which includes
              the ones written before linking existed. ผูก is reserved for the link record. */}
          <span>จัดการแล้ว {fmt(totals.written)} รายการ · {fmt(totals.amount)} ฿</span>
          {/* ⚠️ The open money leads, in rose, and states its own denominator. Before this the
              strip showed only what HAD been saved, so a backlog of half a million baht was
              represented on screen by no pixels at all. */}
          {totals.openBaht > 0 ? (
            <span className="font-medium text-rose-700">
              ยังไม่จัดการ {fmt(totals.openRows)} รายการ · <b>{fmt(totals.openBaht)} ฿</b>
              {totals.openRuns ? <span className="font-normal text-rose-600/80"> ใน {fmt(totals.openRuns)} รอบ</span> : null}
            </span>
          ) : <span className="text-emerald-700 font-medium">จัดการครบทุกรอบแล้ว</span>}
          {totals.reverted ? <span className="text-slate-500">ย้อนกลับแล้ว {fmt(totals.reverted)} รอบ</span> : null}
        </div>
      ) : null}

      {loadErr ? (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-[13px] text-amber-800">{loadErr}</div>
      ) : loading ? (
        <div className="text-[13.5px] text-slate-500 py-6">กำลังโหลด…</div>
      ) : !runs.length ? (
        <div className="text-[13.5px] text-slate-500 py-6">ยังไม่มีการนำเข้า</div>
      ) : !filtered.length ? (
        /* Different from the above and must stay different: "nothing has EVER been imported"
           is a fact about the clinic; "nothing matches" is a fact about four dropdowns. */
        <div className="py-6 text-[13.5px] text-slate-500">
          ไม่พบรอบที่ตรงกับตัวกรอง —{' '}
          <button type="button" onClick={() => setParams((prev) => {
            const n = new URLSearchParams(prev)
            for (const k of ['ci_month', 'ci_source', 'ci_payer', 'ci_status', 'ci_q', 'ci_page']) n.delete(k)
            return n
          }, { replace: true })} className="text-brand-600 hover:underline">ล้างตัวกรองทั้งหมด</button>
        </div>
      ) : (
        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden shadow-sm">
          <table className="w-full text-[13px]">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <Th>รอบ</Th>
                <SortTh col="imported" sort={filters.sort} onSort={(v) => setFilter('ci_sort', v)}>วันที่นำเข้า</SortTh>
                <SortTh col="sent" sort={filters.sort} onSort={(v) => setFilter('ci_sort', v)}>วันที่ส่ง</SortTh>
                <Th>บริษัท</Th><Th>ไฟล์</Th><Th>ที่มา</Th>
                <Th right>แถว</Th><Th right>จัดการแล้ว</Th><Th right>ยังไม่จัดการ</Th><Th>สถานะ</Th><Th />
              </tr>
            </thead>
            <tbody>
              {pageRows.map((r) => (
                /* The WHOLE ROW opens the run, matching BillingTable's convention — a
                   four-character id is a poor click target for the primary action on the
                   page. ⚠️ The รอบ cell used to ALSO be a <Link>, kept so ⌘-click and
                   middle-click could open a run in a new tab; that was removed on request
                   because two click targets for one action inside a clickable row is
                   confusing. New-tab opening went with it. */
                <tr
                  key={r.id}
                  onClick={() => openRun(r)}
                  onKeyDown={(e) => { if (e.key === 'Enter') openRun(r) }}
                  tabIndex={0}
                  role="button"
                  title={`เปิดรอบนำเข้า #${r.id}`}
                  /* ⚠️ border-l-transparent on the UNVIEWED branch is load-bearing: without a
                     placeholder border the marked row alone shifts 2px sideways. */
                  className={`border-t border-slate-100 border-l-2 focus:outline-none cursor-pointer transition-colors ${
                    lastOpened === r.id
                      ? 'bg-violet-50/60 border-l-violet-400 hover:bg-violet-50 focus:bg-violet-50'
                      : 'border-l-transparent hover:bg-brand-50/40 focus:bg-brand-50/40'
                  }`}
                >
                  {/* ⚠️ Plain text, not a link. The whole row already opens the run, so the
                      nested <Link> was a second click target for the same action sitting
                      inside a clickable row. Cost of the change: ⌘-click and middle-click no
                      longer open a run in a new tab, because there is no <a> left to do it. */}
                  {/* ⚠️ The marker lives HERE, not in the trailing w-9 cell. `canDiscard` is
                      true for every `staged` run (rows_written is 0 on all of them), so in
                      that cell the delete button would always win on exactly the runs an
                      operator opens and comes back from — the marker would ship dead. */}
                  <Td className="font-medium text-slate-500 tabular-nums">
                    {r.id}
                    {lastOpened === r.id ? <Eye size={13} className="inline ml-1 text-violet-400 align-text-bottom" /> : null}
                  </Td>
                  <Td>{dt(r.created_at)}</Td>
                  <Td className="whitespace-nowrap">
                    {(() => {
                      const s = sentDate(r)
                      return s ? (
                        <>{s.text} <span className="text-[11px] text-slate-400">{s.note}</span></>
                      ) : <span className="text-slate-400">—</span>
                    })()}
                  </Td>
                  <Td>{r.payer}</Td>
                  <Td className="max-w-[220px] truncate" title={r.file_name}>{r.file_name || '—'}</Td>
                  {/* ⚠️ was a two-arm ternary whose bare fallback rendered ANY unknown
                      source as ทำซ้ำ. The same ternary on ClaimImportRun.jsx is fixed too. */}
                  <Td>{sourceLabel(r.source)}</Td>
                  <Td right>{fmt(r.rows_total)}</Td>
                  {/* ⚠️ `ผูกแล้ว n / N` rather than a bare count: the count alone cannot say
                      whether anything is left, which is the only question this row answers. */}
                  {/* ⚠️ The denominator is runOpen's `total`, not `rows_total` — the same one
                      the run page's tile prints, so the two screens read the same n / N. */}
                  <Td right className="tabular-nums text-slate-500">
                    {fmt(runOpen(r).done)} / {fmt(runOpen(r).total)}
                  </Td>
                  {/* the money still unaccounted for in THIS file — the column that was missing */}
                  <Td right className={runOpen(r).openBaht > 0 ? 'font-semibold text-rose-700' : 'text-slate-400'}>
                    {runOpen(r).openBaht > 0 ? `${fmt(runOpen(r).openBaht)} ฿` : '—'}
                  </Td>
                  <Td><StatusChip run={r} /></Td>
                  <Td className="w-9">
                    {canDiscard(r) ? (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setDeleteErr(''); setPendingDelete(r) }}
                        title="ลบรอบนำเข้านี้"
                        aria-label={`ลบรอบนำเข้า ${r.id}`}
                        className="p-1.5 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors"
                      >
                        <Trash2 size={15} strokeWidth={1.9} />
                      </button>
                    ) : null}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
          {/* Pager renders nothing below 2 pages, so no guard is needed. */}
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 px-4 py-2.5">
            <span className="text-[12.5px] text-slate-500">
              แสดง {fmt((safePage - 1) * PER_PAGE + 1)}–{fmt(Math.min(safePage * PER_PAGE, filtered.length))} จาก {fmt(filtered.length)} รอบ
            </span>
            <Pager page={safePage} pageTotal={pageCount} onChange={setPage} size="sm" />
          </div>
        </div>
      )}

      {pendingDelete ? (
        <ConfirmDialog
          tone="danger"
          title={`ลบรอบนำเข้า ${pendingDelete.id}?`}
          confirmLabel={deleting ? 'กำลังลบ…' : 'ลบรอบนี้'}
          onCancel={() => { if (!deleting) { setPendingDelete(null); setDeleteErr('') } }}
          onConfirm={doDelete}
          message={
            <>
              <div>
                {pendingDelete.payer} · {pendingDelete.file_name || 'ไม่มีชื่อไฟล์'} ·{' '}
                {fmt(pendingDelete.rows_total)} แถว
              </div>
              <div className="mt-1.5">
                รอบนี้<b>ยังไม่ได้บันทึกอะไรลงระบบ</b> การลบจะไม่กระทบยอดค้างรับหรือรายการเรียกเก็บใด ๆ
                {' '}และไฟล์ต้นฉบับยังเก็บไว้เหมือนเดิม อัปโหลดไฟล์เดิมซ้ำได้
              </div>
              {/* ⚠️ The courier labels a Gmail thread as done BEFORE it posts, so a deleted
                  email-sourced run will never be re-delivered by itself. Saying so here is
                  the difference between discarding a draft and losing a settlement. */}
              {pendingDelete.source === 'email_robot' ? (
                <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-2 text-amber-800">
                  รอบนี้มาจาก<b>อีเมลอัตโนมัติ</b> ระบบได้ทำเครื่องหมายอีเมลฉบับนั้นว่าจัดการแล้ว
                  {' '}<b>จะไม่ส่งเข้ามาใหม่เอง</b> ถ้าต้องการนำเข้าอีกครั้งต้องอัปโหลดไฟล์เอง
                </div>
              ) : null}
              {deleteErr ? (
                <div className="mt-2 rounded-lg border border-rose-200 bg-rose-50 px-2.5 py-2 text-rose-700">
                  {deleteErr}
                </div>
              ) : null}
            </>
          }
        />
      ) : null}
    </div>
  )
}

/**
 * A clickable header. Click cycles ใหม่→เก่า, เก่า→ใหม่, back to the default order.
 * วันที่นำเข้า's default IS ใหม่→เก่า (the server's order), so its arrow shows that unasked.
 */
function SortTh({ col, sort, onSort, children }) {
  const [c, dir] = String(sort || '').split('_')
  const active = c === col || (!sort && col === 'imported')
  const d = c === col ? dir : (active ? 'desc' : '')
  const next = c !== col ? (col === 'imported' && !sort ? `${col}_asc` : `${col}_desc`)
    : dir === 'desc' ? `${col}_asc` : ''
  const Icon = d === 'asc' ? ArrowUp : d === 'desc' ? ArrowDown : ArrowUpDown
  return (
    <th className="px-3 py-2 font-medium text-left" aria-sort={d === 'asc' ? 'ascending' : d === 'desc' ? 'descending' : 'none'}>
      <button type="button" onClick={() => onSort(next)}
        title={d === 'desc' ? 'เรียงเก่า→ใหม่' : d === 'asc' ? 'กลับลำดับปกติ' : 'เรียงใหม่→เก่า'}
        className={`inline-flex items-center gap-1 hover:text-slate-900 ${active ? 'text-slate-900' : ''}`}>
        {children}
        <Icon size={13} className={active ? 'text-brand-600' : 'text-slate-400'} />
      </button>
    </th>
  )
}

function Th({ children, right }) {
  return <th className={`px-3 py-2 font-medium ${right ? 'text-right' : 'text-left'}`}>{children}</th>
}
function Td({ children, right, className = '' }) {
  return <td className={`px-3 py-2 ${right ? 'text-right tabular-nums' : ''} ${className}`}>{children}</td>
}
